import { z } from "zod";
import type { StateCreator } from "zustand/vanilla";

/**
 * segmentGroupEditTargetSchema is the segment-group form's open state: the itinerary
 * the group belongs to, the group being edited (null while creating a new one), and
 * the run the range picker opens on. Zod-first because the value is shared with the
 * group modal tool input and validated when an agent-side chain opens the form; a
 * null target (form closed) lives outside the schema as the absence of a value.
 *
 * `seedSegmentIds` is a seed, not the truth: the picker takes it as its starting run
 * and owns every subsequent extend/shrink locally, so the store never mirrors the
 * form's in-progress selection.
 */
export const segmentGroupEditTargetSchema = z.object({
  groupId: z.string().nullable(),
  itineraryId: z.string(),
  seedSegmentIds: z.array(z.string()),
});

/**
 * SegmentGroupEditTarget is the open segment-group form's target, derived from
 * {@link segmentGroupEditTargetSchema}. The slot holds this or null (form closed).
 */
export type SegmentGroupEditTarget = z.infer<typeof segmentGroupEditTargetSchema>;

/**
 * SegmentGroupEditSliceState is the open segment-group form slot.
 */
export interface SegmentGroupEditSliceState {
  /**
   * segmentGroupEdit is the open group form's state (null = the form is closed). A
   * null `groupId` opens it in create mode seeded with the run the entry point
   * proposed; a non-null one opens it on that group. The form's name, colour, speed,
   * and working run all stay in the surface's own state, so this slot is a minimal
   * open/closed pointer like the share and directions slots. Set only through the
   * tool bus, like every other UiAction.
   */
  segmentGroupEdit: SegmentGroupEditTarget | null;
}

/**
 * SegmentGroupEditSliceActions is the whole-slot setter that drives the group form,
 * reached only through the tool bus.
 */
export interface SegmentGroupEditSliceActions {
  /**
   * setSegmentGroupEdit opens the group form with the given target, or closes it when
   * passed null. The whole-slot setter the modal open/close tools drive, mirroring
   * setShare — the form keeps no narrower fields in the store.
   */
  setSegmentGroupEdit: (target: SegmentGroupEditTarget | null) => void;
}

/**
 * segmentGroupEditInitialState seeds the group form closed.
 */
export const segmentGroupEditInitialState: SegmentGroupEditSliceState = {
  segmentGroupEdit: null,
};

/**
 * createSegmentGroupEditSlice builds the group form action. The single whole-slot
 * setter opens the form on a group (or on a proposed run for a new one) or closes it,
 * mirroring the share and directions slices' open/close shape.
 */
export const createSegmentGroupEditSlice: StateCreator<
  SegmentGroupEditSliceState & SegmentGroupEditSliceActions,
  [],
  [],
  SegmentGroupEditSliceActions
> = (set) => ({
  setSegmentGroupEdit: (segmentGroupEdit) => set({ segmentGroupEdit }),
});

/**
 * selectSegmentGroupEdit is the open group form's target, or null when it is closed.
 * The mount-guard selector the modal reads to decide whether to render at all —
 * narrow so the guard re-renders only on the open/close flip, mirroring selectShare.
 */
export const selectSegmentGroupEdit = (
  state: SegmentGroupEditSliceState,
): SegmentGroupEditTarget | null => state.segmentGroupEdit;
